import React, { useState } from "react";
import "./DisplayNameSetup.css";
import { useFirebase } from "../contexts/FirebaseContext";
import { useLanguage } from "../contexts/LanguageContext";

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "de", label: "Deutsch" },
];

const DisplayNameSetup = ({ onComplete, onCancel, initialName = "" }) => {
  const { updateDisplayName } = useFirebase();
  const { t, language, changeLanguage } = useLanguage();
  const [displayName, setDisplayName] = useState(initialName);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedName = displayName.trim();

    if (!trimmedName) {
      setError(t("displayNameRequired"));
      return;
    }

    if (trimmedName.length < 2) {
      setError(t("displayNameTooShort"));
      return;
    }

    if (trimmedName.length > 20) {
      setError(t("displayNameTooLong"));
      return;
    }

    setIsSubmitting(true);
    try {
      await updateDisplayName(trimmedName);
      onComplete();
    } catch (err) {
      console.error("Error setting display name:", err);
      setError(t("displayNameError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="display-name-setup-container">
      <div className="display-name-setup-card">
        <div
          className="display-name-language"
          role="group"
          aria-label={t("chooseLanguage")}
        >
          <p className="display-name-language-label">{t("chooseLanguage")}</p>
          <div className="display-name-language-options">
            {LANGUAGES.map(({ id, label }) => {
              const isActive = language === id;
              return (
                <button
                  key={id}
                  type="button"
                  className={`display-name-language-btn ${
                    isActive ? "is-active" : ""
                  }`}
                  aria-pressed={isActive}
                  disabled={isSubmitting}
                  onClick={() => changeLanguage(id)}
                >
                  <span className="display-name-language-code">
                    {id.toUpperCase()}
                  </span>
                  <span className="display-name-language-name">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <h1 className="display-name-title">👤 {t("setDisplayName")}</h1>
        <p className="display-name-description">{t("displayNameDescription")}</p>

        <form onSubmit={handleSubmit} className="display-name-form">
          <div className="display-name-input-group">
            <input
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setError("");
              }}
              placeholder={t("displayNamePlaceholder")}
              className="display-name-input"
              maxLength={20}
              autoFocus
              disabled={isSubmitting}
            />
            {error && <p className="display-name-error">{error}</p>}
          </div>

          {onCancel ? (
            <div className="display-name-actions">
              <button
                type="button"
                className="display-name-cancel-button"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="display-name-submit-button"
                disabled={isSubmitting || !displayName.trim()}
              >
                {isSubmitting ? t("saving") : t("saveDisplayName")}
              </button>
            </div>
          ) : (
            <button
              type="submit"
              className="display-name-submit-button display-name-submit-full"
              disabled={isSubmitting || !displayName.trim()}
            >
              {isSubmitting ? t("saving") : t("saveDisplayName")}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default DisplayNameSetup;
